import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { toast } from "react-toastify";
import { PhoneNumberUtil } from "google-libphonenumber";

import { ActionButton, FormDialog, InputField, PhoneInput, SelectField } from "../v3";
import { AdminService } from "../../services/admin.service";
import { RolesService } from "../../services";
import { userValidationRules } from "../../pages/administration/validation";
import { formatPayload } from "../../utils";

const phoneUtil = PhoneNumberUtil.getInstance();

const isPhoneValid = (phone) => {
  try {
    return phoneUtil.isValidNumber(phoneUtil.parseAndKeepRawInput(phone));
  } catch {
    return false;
  }
};

export const AddEditUser = ({ open, onClose, userId, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [touched, setTouched] = useState(false);
  const [userDetails, setUserDetails] = useState({
    username: undefined,
    firstName: undefined,
    lastName: undefined,
    roleId: undefined,
    primaryPhone: undefined,
    password: undefined,
    primaryEmail: undefined,
    primaryAddress: {
      locality: undefined,
      state: undefined,
      pinCode: undefined,
      country: undefined,
    },
  });

  const isValid = isPhoneValid(userDetails.primaryPhone);
  const showError = touched && !!userDetails.primaryPhone && !isValid;

  useEffect(() => {
    if (open && userId) {
      fetchData();
    } else if (open && !userId) {
      handleClearForm();
    }
  }, [open, userId]);

  const fetchData = async () => {
    try {
      const { data: response } = await AdminService.getUserDetails(userId);
      setUserDetails({
        username: response?.username || undefined,
        firstName: response?.firstName || undefined,
        lastName: response?.lastName || undefined,
        roleId: response?.roleId || undefined,
        primaryPhone: response?.primaryPhone || undefined,
        primaryEmail: response?.primaryEmail || undefined,
        primaryAddress: {
          locality: response?.primaryAddress?.locality || undefined,
          state: response?.primaryAddress?.state || undefined,
          pinCode: response?.primaryAddress?.pinCode || undefined,
          country: response?.primaryAddress?.country || undefined,
        },
      });
      setSelectedRole(response?.role || null);
    } catch (error) {
      toast("Failed to fetch user details", { type: "error" });
      console.error("🚀 ~ fetchUserData ~ error:", error);
    }
  };

  const handleClearForm = () => {
    setUserDetails({
      username: undefined,
      firstName: undefined,
      lastName: undefined,
      roleId: undefined,
      primaryPhone: undefined,
      password: undefined,
      primaryEmail: undefined,
      primaryAddress: {
        locality: undefined,
        state: undefined,
        pinCode: undefined,
        country: undefined,
      },
    });
    setSelectedRole(null);
  };

  const updateDetailsState = (event) => {
    const { name, value } = event.target;
    if (["locality", "state", "pinCode", "country"].includes(name)) {
      setUserDetails({
        ...userDetails,
        primaryAddress: { ...userDetails.primaryAddress, [name]: value },
      });
    } else {
      setUserDetails({ ...userDetails, [name]: value });
    }
  };
  const handleRoleChange = (selected) => {
    setSelectedRole(selected);
    setUserDetails((prev) => ({ ...prev, roleId: selected?.id || null }));
  };

  const togglePasswordVisibility = () => setShowPassword((prev) => !prev);

  const handleCloseModal = () => {
    handleClearForm();
    setErrors({});
    onClose();
  };

  const formatUserPayload = (details, isEdit) => {
    const formatted = { ...details };
    const payload = formatPayload(formatted);

    if (payload.primaryAddress) {
      const address = payload.primaryAddress;
      const keys = ["locality", "state", "pinCode", "country"];
      const allEmpty = keys.every((key) => !address[key] || address[key].toString().trim() === "");

      if (allEmpty) {
        // all empty → set empty string for each key
        keys.forEach((key) => {
          address[key] = "";
        });
      } else {
        // some have value → fill missing with empty string
        keys.forEach((key) => {
          if (!address[key] || address[key].toString().trim() === "") {
            address[key] = "";
          }
        });
      }
    }

    return payload;
  };

  const handleSaveUser = async () => {
    setErrors({});

    const formattedData = formatUserPayload(userDetails, !!userId);

    const validationErrors = userValidationRules(formattedData, userId);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    try {
      if (userId) await AdminService.updateUser(userId, formattedData);
      else
        await AdminService.createUser({
          ...formattedData,
          type: "USER",
        });

      toast.success(`User ${userId ? "updated" : "added"} successfully`);
      if (onSuccess) onSuccess();
      handleCloseModal();
    } catch (error) {
      let errorMessage = `Error ${userId ? "updating" : "adding"} user`;
      const response = error?.response?.data;

      if (Array.isArray(response?.message)) {
        errorMessage = response.message.join(", ");
      } else if (typeof response?.message === "string") {
        errorMessage = response.message;
      }

      toast(errorMessage, { type: "error" });
      console.error("🚀 ~ handleSaveUser ~ error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormDialog
      open={open}
      title={userId ? "Edit User" : "Add User"}
      actions={
        <>
          <ActionButton buttonText="Cancel" onButtonClick={handleCloseModal} variant="outlined" />
          <ActionButton buttonText="Save" onButtonClick={handleSaveUser} loading={loading} />
        </>
      }
    >
      <Box mt={1}>
        <Box display="flex" gap={2}>
          <InputField
            required
            label="First Name"
            name="firstName"
            value={userDetails.firstName}
            onChange={updateDetailsState}
            error={!!errors.firstName}
            helperText={errors.firstName}
            fullWidth
          />
          <InputField
            required
            label="Last Name"
            name="lastName"
            value={userDetails.lastName}
            onChange={updateDetailsState}
            error={!!errors.lastName}
            helperText={errors.lastName}
            fullWidth
          />
        </Box>

        {/* Row 2 */}
        <Box display="flex" gap={2}>
          <SelectField
            label="Role"
            value={selectedRole}
            getData={RolesService.getRoles}
            getDataStaticFilters={{ sortBy: "name", sortOrder: "asc" }}
            required
            error={!!errors.roleId}
            helperText={errors.roleId}
            onChange={handleRoleChange}
            style={{ width: "100%" }}
          />
          <InputField
            required
            label="Username"
            name="username"
            value={userDetails.username}
            onChange={updateDetailsState}
            error={!!errors.username}
            helperText={errors.username}
            fullWidth
          />
        </Box>

        {/* Row 3 */}
        <Box display="flex" gap={2}>
          <InputField
            required
            label="Email"
            name="primaryEmail"
            type="email"
            value={userDetails.primaryEmail}
            onChange={updateDetailsState}
            error={!!errors.primaryEmail}
            helperText={errors.primaryEmail}
            fullWidth
          />
          <PhoneInput
            label="Mobile Number"
            defaultCountry="in"
            value={userDetails.primaryPhone}
            size="small"
            fullWidth
            required
            onChange={(e164) => setUserDetails((prev) => ({ ...prev, primaryPhone: e164 }))}
            onBlur={() => setTouched(true)}
            error={showError}
            helperText={showError ? "Enter a valid phone number" : " "}
          />
        </Box>

        {/* Row 4 */}
        <Box display="flex" gap={2}>
          {!userId && (
            <InputField
              required
              showPassword={showPassword}
              togglePasswordVisibility={togglePasswordVisibility}
              label="Password"
              name="password"
              type="password"
              value={userDetails.password}
              onChange={updateDetailsState}
              error={!!errors.password}
              helperText={errors.password}
              autoComplete="new-password"
              fullWidth
            />
          )}
          <InputField
            label="Locality"
            name="locality"
            value={userDetails.primaryAddress.locality}
            onChange={updateDetailsState}
            fullWidth
          />
        </Box>

        {/* Row 5 */}
        <Box display="flex" gap={2}>
          <InputField
            label="State"
            name="state"
            value={userDetails.primaryAddress.state}
            onChange={updateDetailsState}
            fullWidth
          />
          <InputField
            label="Country"
            name="country"
            value={userDetails.primaryAddress.country}
            onChange={updateDetailsState}
            fullWidth
          />
          <InputField
            label="Pin code"
            name="pinCode"
            type="number"
            value={userDetails.primaryAddress.pinCode}
            onChange={updateDetailsState}
            error={!!errors?.primaryAddress?.pinCode}
            helperText={errors?.primaryAddress?.pinCode}
            fullWidth
          />
        </Box>
      </Box>
    </FormDialog>
  );
};
